import React from 'react';
import { createPortal } from 'react-dom';
import Tooltip from "../tooltip";

import {Loading} from '../loading/index';
import TextOrStatus from '../textOrStatus';

import cancelaIcon from '../../assets/icons/icon-trash-red.png';
import arrowDownIcon from '../../assets/icons/icon-arrow-down-alone-gray.png';

import * as S from './stylesV2';
import { theme } from '../../styles/theme';
import { BodyM, TaglineP, TaglinePSpan } from '../../styles/stylesTypography';

const cellLabelStyle = {
  width: 'auto',
  height: 'auto',
  gap: '4px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
};

const textInCellLabelStyle = {
  height: '23px',
  display: 'flex',
  alignItems: 'center'
};

const areHeightsEqual = (previous = [], next = []) => {
  if (previous.length !== next.length) return false;
  for (let i = 0; i < previous.length; i += 1) {
    if (previous[i] !== next[i]) return false;
  }
  return true;
};

const FILTER_OPERATORS = [
  { value: 'contains', label: 'Contém' },
  { value: 'notContains', label: 'Não contém' },
  { value: 'equals', label: 'Igual' },
  { value: 'notEquals', label: 'Diferente' },
  { value: 'startsWith', label: 'Começa com' },
  { value: 'endsWith', label: 'Termina com' },
  { value: 'isEmpty', label: 'Vazio' },
  { value: 'isNotEmpty', label: 'Não vazio' }
];

const DEFAULT_FILTER_STATE = {
  mode: 'contains',
  value: '',
  combine: 'and',
  secondaryMode: 'contains',
  secondaryValue: '',
  selectedValues: []
};

const getNormalizedValue = (value) => String(value ?? '').trim().toLowerCase();
const isEmptyMode = (mode) => mode === 'isEmpty' || mode === 'isNotEmpty';

const modeRequiresInput = (mode) => !isEmptyMode(mode);

const evaluateFilterCondition = (rowValue, mode, filterValue) => {
  const normalizedRow = getNormalizedValue(rowValue);
  const normalizedFilter = getNormalizedValue(filterValue);

  if (modeRequiresInput(mode) && !normalizedFilter) return true;

  switch (mode) {
    case 'contains':
      return normalizedRow.includes(normalizedFilter);
    case 'notContains':
      return !normalizedRow.includes(normalizedFilter);
    case 'equals':
      return normalizedRow === normalizedFilter;
    case 'notEquals':
      return normalizedRow !== normalizedFilter;
    case 'startsWith':
      return normalizedRow.startsWith(normalizedFilter);
    case 'endsWith':
      return normalizedRow.endsWith(normalizedFilter);
    case 'isEmpty':
      return normalizedRow.length === 0;
    case 'isNotEmpty':
      return normalizedRow.length > 0;
    default:
      return true;
  }
};

const FilterIcon = ({ active = false }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 6h16l-6.5 7.2V19l-3 1v-6.8L4 6Z"
      stroke={active ? '#0B78E3' : '#FFFFFF'}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TableWithLabelBlackV2 = ({
  columns,
  data,
  agronomos = [],
  items,
  showAll = false,
  page = 1,
  itemsPerPage = 4,
  serverSide = false,
  onCountChange,
  getRowId,
  onRowClick,
  isRowDisabled,
  getRowStyle,
  stripedRows = true,
  stripedEvenColor = '#FFFFFF',
  stripedOddColor = '#f9f9f9',
  actionsColumn,
  enableDefaultRowNavigation = true,
  getRowClickHref,
  enableColumnFilters = false,
  onColumnFiltersChange,
  useServerSideColumnFilters = false,
  isLoading = false,
  emptyMessage = ''
}) => {
  const cellRefs = React.useRef([]);
  const scrollCellRefs = React.useRef([]);
  const filtersPanelRef = React.useRef(null);
  const filterTriggerRefs = React.useRef({});
  const realTableScrollRef = React.useRef(null);
  const fakeScrollbarRef = React.useRef(null);
  const isDraggingFakeScrollbarRef = React.useRef(false);
  const dragStartClientXRef = React.useRef(0);
  const dragStartThumbOffsetRef = React.useRef(0);
  const [rowHeights, setRowHeights] = React.useState([]);
  const [overflowMap, setOverflowMap] = React.useState({});
  const [columnFilters, setColumnFilters] = React.useState({});
  const [activeFilterColumnKey, setActiveFilterColumnKey] = React.useState(null);
  const [filterPanelPosition, setFilterPanelPosition] = React.useState({ top: 0, left: 0 });
  const [showFakeScrollbar, setShowFakeScrollbar] = React.useState(false);
  const [fakeTrackWidth, setFakeTrackWidth] = React.useState(0);
  const [fakeThumbWidth, setFakeThumbWidth] = React.useState(0);
  const [fakeThumbOffset, setFakeThumbOffset] = React.useState(0);
  const [isDraggingFakeScrollbar, setIsDraggingFakeScrollbar] = React.useState(false);

  const getFilterPanelMetricsByColumnKey = React.useCallback((columnKey) => {
    const columnDef = Array.isArray(columns)
      ? columns.find((col) => (col?.key ?? '') === columnKey)
      : null;
    const isCheckbox = columnDef?.filterType === 'checkbox';

    if (isCheckbox) {
      const customWidth = columnDef?.filterPanelWidth;
      const normalizedCustomWidth = typeof customWidth === 'number'
        ? customWidth
        : parseInt(String(customWidth ?? ''), 10);
      return {
        panelWidth: Number.isFinite(normalizedCustomWidth) ? normalizedCustomWidth : 320,
        panelHeight: 360
      };
    }

    return {
      panelWidth: 230,
      panelHeight: 260
    };
  }, [columns]);

  const setFilterTriggerRef = React.useCallback((columnKey, element) => {
    if (!columnKey) return;
    if (element) {
      filterTriggerRefs.current[columnKey] = element;
      return;
    }
    delete filterTriggerRefs.current[columnKey];
  }, []);

  const updateFilterPanelPosition = React.useCallback((columnKey) => {
    if (typeof window === 'undefined' || !columnKey) return;
    const trigger = filterTriggerRefs.current[columnKey];
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const { panelWidth, panelHeight } = getFilterPanelMetricsByColumnKey(columnKey);
    const gap = 10;

    let left = rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));

    let top = rect.bottom + gap;
    if (top + panelHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - panelHeight - gap);
    }

    setFilterPanelPosition((previous) => {
      if (previous.top === top && previous.left === left) return previous;
      return { top, left };
    });
  }, [getFilterPanelMetricsByColumnKey]);

  const setCellRef = (rowIdx, colIdx, el) => {
    if (!cellRefs.current[rowIdx]) {
      cellRefs.current[rowIdx] = [];
    }
    cellRefs.current[rowIdx][colIdx] = el;
  };

  const setScrollCellRef = (rowIdx, colIdx, el) => {
    if (!scrollCellRefs.current[rowIdx]) {
      scrollCellRefs.current[rowIdx] = [];
    }
    scrollCellRefs.current[rowIdx][colIdx] = el;
  };

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);

    const opcoesData = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
    const dataFormatada = data.toLocaleDateString('pt-BR', opcoesData);

    const opcoesHora = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' };
    const horaFormatada = data.toLocaleTimeString('pt-BR', opcoesHora);

    return `${dataFormatada.replace('.', '').split(' de ').join('/')}, ${horaFormatada}`;
  };

  const isGeneric = Array.isArray(columns) && columns.length > 0;
  const normalizedColumns = Array.isArray(columns) ? columns : [];
  const hasActionsColumnRender = typeof actionsColumn?.render === 'function';
  const hasActionsColumnButtons = Array.isArray(actionsColumn?.buttons) && actionsColumn.buttons.length > 0;
  const hasActionsColumn = actionsColumn?.enabled === true && (hasActionsColumnRender || hasActionsColumnButtons);
  const actionsPosition = actionsColumn?.position === 'last' ? 'last' : 'first';

  const getActionButtons = React.useCallback((row, rowIdx) => {
    const buttons = Array.isArray(actionsColumn?.buttons) ? actionsColumn.buttons : [];
    return buttons.filter((button) => {
      if (typeof button?.isVisible === 'function') {
        return !!button.isVisible(row, rowIdx);
      }
      if (typeof button?.visible === 'boolean') {
        return button.visible;
      }
      return true;
    });
  }, [actionsColumn]);

  const getActionButtonDisabled = React.useCallback((button, row, rowIdx) => {
    if (typeof button?.isDisabled === 'function') return !!button.isDisabled(row, rowIdx);
    if (typeof button?.disabled === 'boolean') return button.disabled;
    return false;
  }, []);

  const getActionButtonLoading = React.useCallback((button, row, rowIdx) => {
    if (typeof button?.isLoading === 'function') return !!button.isLoading(row, rowIdx);
    if (typeof button?.loading === 'boolean') return button.loading;
    return false;
  }, []);

  const getActionButtonHref = React.useCallback((button, row, rowIdx) => {
    return typeof button?.href === 'function' ? button.href(row, rowIdx) : button?.href;
  }, []);

  const getActionButtonTooltip = React.useCallback((button, row, rowIdx) => {
    const isLoading = getActionButtonLoading(button, row, rowIdx);
    if (isLoading) {
      if (typeof button?.loadingTooltip === 'function') return button.loadingTooltip(row, rowIdx);
      if (button?.loadingTooltip) return button.loadingTooltip;
    }
    if (typeof button?.tooltip === 'function') return button.tooltip(row, rowIdx);
    return button?.tooltip;
  }, [getActionButtonLoading]);

  const handleActionButtonClick = React.useCallback((event, button, row, rowIdx) => {
    event.stopPropagation();
    const isLoading = getActionButtonLoading(button, row, rowIdx);
    const isDisabled = getActionButtonDisabled(button, row, rowIdx);
    if (isLoading || isDisabled) return;

    if (typeof button?.onClick === 'function') {
      button.onClick(row, rowIdx, event);
      return;
    }

    const href = getActionButtonHref(button, row, rowIdx);
    if (href) {
      window.location.href = href;
    }
  }, [getActionButtonDisabled, getActionButtonHref, getActionButtonLoading]);

  const renderActionsContent = React.useCallback((row, rowIdx) => {
    if (hasActionsColumnRender) {
      return actionsColumn.render(row, rowIdx);
    }

    if (!hasActionsColumnButtons) return null;

    const buttons = getActionButtons(row, rowIdx);
    if (buttons.length === 0) return null;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: actionsColumn?.buttonsGap ?? '8px'
        }}
      >
        {buttons.map((button, buttonIdx) => {
          const loading = getActionButtonLoading(button, row, rowIdx);
          const explicitDisabled = getActionButtonDisabled(button, row, rowIdx);
          const href = getActionButtonHref(button, row, rowIdx);
          const hasActionTarget = typeof button?.onClick === 'function' || !!href;
          const disabled = explicitDisabled || loading || !hasActionTarget;
          const tooltip = getActionButtonTooltip(button, row, rowIdx);
          const iconSrc = typeof button?.icon === 'function' ? button.icon(row, rowIdx) : button?.icon;
          const iconAlt = button?.iconAlt ?? button?.ariaLabel ?? 'Ícone de ação';
          const iconWidth = button?.iconWidth ?? '16px';
          const iconHeight = button?.iconHeight ?? '16px';
          const loadingSize = button?.loadingSize ?? actionsColumn?.loadingSize ?? '16px';
          const loadingColor = button?.loadingColor ?? actionsColumn?.loadingColor ?? theme.colors.green.Verde_AgroReceita;
          const loadingContent = typeof button?.renderLoading === 'function'
            ? button.renderLoading(row, rowIdx)
            : (button?.loadingContent ?? (
              <Loading
                size={loadingSize}
                color={loadingColor}
              />
            ));
          const contentNode = loading && loadingContent
            ? loadingContent
            : (iconSrc ? (
              <img
                src={iconSrc}
                alt={iconAlt}
                style={{
                  width: iconWidth,
                  height: iconHeight,
                  filter: loading ? 'grayscale(100%)' : 'none'
                }}
              />
            ) : null);
          const buttonStyle = {
            all: 'unset',
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled ? 'default' : 'pointer',
            opacity: loading ? 0.6 : (disabled ? 0.45 : 1),
            ...(button?.style ?? {})
          };

          const content = (
            <button
              type="button"
              aria-label={button?.ariaLabel ?? (typeof tooltip === 'string' ? tooltip : 'Ação')}
              aria-busy={loading ? 'true' : 'false'}
              onClick={(event) => handleActionButtonClick(event, button, row, rowIdx)}
              style={buttonStyle}
            >
              {contentNode}
            </button>
          );

          if (!tooltip) {
            return <React.Fragment key={button?.key ?? buttonIdx}>{content}</React.Fragment>;
          }

          return (
            <Tooltip
              key={button?.key ?? buttonIdx}
              content={tooltip}
              animation="fade"
              delay={[0, 100]}
              arrow
              placement="bottom"
              appendTo={() => document.body}
              zIndex={99999}
            >
              {content}
            </Tooltip>
          );
        })}
      </div>
    );
  }, [
    actionsColumn,
    getActionButtons,
    getActionButtonDisabled,
    getActionButtonHref,
    getActionButtonLoading,
    getActionButtonTooltip,
    handleActionButtonClick,
    hasActionsColumnButtons,
    hasActionsColumnRender
  ]);

  const computedColumns = React.useMemo(() => {
    if (!isGeneric) return normalizedColumns;
    if (!hasActionsColumn) return normalizedColumns;

    const actionCol = {
      key: actionsColumn?.key ?? '__actions__',
      label: actionsColumn?.label ?? 'AÇÕES',
      width: actionsColumn?.width ?? '140px',
      minWidth: actionsColumn?.minWidth ?? actionsColumn?.width ?? '140px',
      maxWidth: actionsColumn?.maxWidth,
      justifyContent: actionsColumn?.justifyContent ?? 'center',
      render: (row, rowIdx) => renderActionsContent(row, rowIdx)
    };

    return actionsPosition === 'last'
      ? [...normalizedColumns, actionCol]
      : [actionCol, ...normalizedColumns];
  }, [isGeneric, normalizedColumns, hasActionsColumn, actionsPosition, actionsColumn, renderActionsContent]);

  const list = isGeneric
    ? (Array.isArray(data) ? data : (Array.isArray(items) ? items : []))
    : (Array.isArray(agronomos) && agronomos.length > 0 ? agronomos : (Array.isArray(items) ? items : []));

  const pickPrimaryCrea = (agronomo) => {
    const creas = Array.isArray(agronomo?.agro_creas) ? agronomo.agro_creas : [];
    return creas.find((crea) => crea?.status === 1) ?? creas[0] ?? null;
  };

  const getDisplayCreas = (agronomo) => {
    const creas = Array.isArray(agronomo?.agro_creas) ? agronomo.agro_creas : [];
    const active = creas.filter((crea) => crea?.status === 1);
    const inactive = creas.filter((crea) => crea?.status !== 1);
    return [...active, ...inactive];
  };

  const deriveStatus = (agronomo) => {
    const raw =
      agronomo?.status ??
      agronomo?.agronomist_status ??
      agronomo?.agronomo_status ??
      agronomo?.agro_agronomists_status ??
      agronomo?.agro_agronomist_status ??
      agronomo?.agro_creas_status ??
      agronomo?.crea_status ??
      pickPrimaryCrea(agronomo)?.status;

    if (raw == null) return { known: false, isActive: false };
    if (typeof raw === 'boolean') return { known: true, isActive: raw };

    if (typeof raw === 'number') {
      if (raw === 0) return { known: true, isActive: false };
      if (raw === 1) return { known: true, isActive: true };
      return { known: true, isActive: raw > 0 };
    }

    const normalized = String(raw).trim().toLowerCase();
    if (!normalized) return { known: false, isActive: false };
    if (normalized === '0') return { known: true, isActive: false };
    if (normalized === '1') return { known: true, isActive: true };
    if (normalized.includes('inativo')) return { known: true, isActive: false };
    if (normalized.includes('ativo')) return { known: true, isActive: true };
    return { known: true, isActive: false };
  };

  const getStripedRowStyle = React.useCallback((rowIdx, customStyle) => {
    const stripeStyle = stripedRows
      ? { backgroundColor: rowIdx % 2 === 0 ? stripedEvenColor : stripedOddColor }
      : {};
    return {
      ...stripeStyle,
      ...(customStyle ?? {})
    };
  }, [stripedRows, stripedEvenColor, stripedOddColor]);

  const formatRegistro = (crea, agronomo) => {
    const uf = crea?.estado ?? agronomo?.estado ?? '-';
    return uf ? String(uf).toUpperCase() : '-';
  };

  const formatCreaNumero = (crea, agronomo) => {
    const numero = crea?.crea ?? agronomo?.crea_numero ?? '-';
    return numero ?? '-';
  };

  const formatTipoRegistro = (crea) => {
    const tipo = crea?.tipo ?? '-';
    return tipo ? String(tipo).toUpperCase() : '-';
  };

  const getColumnFilterState = React.useCallback((columnKey) => ({
    ...DEFAULT_FILTER_STATE,
    ...(columnFilters[columnKey] ?? {})
  }), [columnFilters]);

  const getColumnDefinitionByKey = React.useCallback((columnKey) => {
    if (!columnKey) return null;
    return computedColumns.find((col) => (col?.key ?? '') === columnKey) ?? null;
  }, [computedColumns]);

  const isCheckboxFilterColumn = React.useCallback((columnKey) => {
    const column = getColumnDefinitionByKey(columnKey);
    return column?.filterType === 'checkbox';
  }, [getColumnDefinitionByKey]);

  const isColumnFilterActive = React.useCallback((columnKey) => {
    const current = getColumnFilterState(columnKey);
    if (isCheckboxFilterColumn(columnKey)) {
      return Array.isArray(current.selectedValues) && current.selectedValues.length > 0;
    }
    if (isEmptyMode(current.mode)) return true;
    return getNormalizedValue(current.value).length > 0;
  }, [getColumnFilterState, isCheckboxFilterColumn]);

  const handleFilterChange = React.useCallback((columnKey, patch) => {
    setColumnFilters((previous) => ({
      ...previous,
      [columnKey]: {
        ...DEFAULT_FILTER_STATE,
        ...(previous[columnKey] ?? {}),
        ...patch
      }
    }));
  }, []);

  const clearColumnFilter = React.useCallback((columnKey) => {
    setColumnFilters((previous) => {
      if (!previous?.[columnKey]) return previous;
      const next = { ...previous };
      delete next[columnKey];
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (typeof onColumnFiltersChange !== 'function') return;
    if (!enableColumnFilters) {
      onColumnFiltersChange({});
      return;
    }
    onColumnFiltersChange(columnFilters);
  }, [columnFilters, enableColumnFilters, onColumnFiltersChange]);

  React.useEffect(() => {
    if (!isLoading) return;
    setActiveFilterColumnKey(null);
  }, [isLoading]);

  const getGenericColumnFilterValue = React.useCallback((row, rowIdx, columnKey) => {
    const column = computedColumns.find((col) => (col?.key ?? '') === columnKey);
    if (!column) return '';
    if (typeof column?.filterBy === 'function') {
      return column.filterBy(row, rowIdx);
    }
    const raw = row?.[columnKey];
    if (raw == null) return '';
    if (Array.isArray(raw)) return raw.join(' ');
    if (typeof raw === 'object') return JSON.stringify(raw);
    return String(raw);
  }, [computedColumns]);

  const getNonGenericColumnFilterValue = React.useCallback((agronomo, columnKey) => {
    const displayCreas = getDisplayCreas(agronomo);
    switch (columnKey) {
      case 'nome':
        return agronomo?.agronomist_nome ?? agronomo?.nome ?? '';
      case 'documento':
        return displayCreas.map((crea) => formatCreaNumero(crea, agronomo)).join(' | ');
      case 'tipo':
        return displayCreas.map((crea) => formatTipoRegistro(crea)).join(' | ');
      case 'ufRegistro':
        return displayCreas.map((crea) => formatRegistro(crea, agronomo)).join(' | ');
      case 'status': {
        if (displayCreas.length > 0) {
          return displayCreas.map((crea) => (crea?.status === 1 ? 'Ativo' : 'Inativo')).join(' | ');
        }
        return deriveStatus(agronomo).isActive ? 'Ativo' : 'Inativo';
      }
      case 'ultimaAtualizacao':
        return agronomo?.modified ?? agronomo?.updated_at ?? agronomo?.updatedAt ?? '';
      default:
        return '';
    }
  }, [deriveStatus]);

  const filteredList = React.useMemo(() => {
    if (!enableColumnFilters) return list;
    if (useServerSideColumnFilters) return list;

    const activeFilterKeys = Object.keys(columnFilters).filter((columnKey) => isColumnFilterActive(columnKey));
    if (activeFilterKeys.length === 0) return list;

    return list.filter((row, rowIdx) => activeFilterKeys.every((columnKey) => {
      const filterState = getColumnFilterState(columnKey);
      if (isCheckboxFilterColumn(columnKey)) {
        const selectedValues = Array.isArray(filterState.selectedValues) ? filterState.selectedValues : [];
        if (selectedValues.length === 0) return true;
        const rowValue = isGeneric
          ? getGenericColumnFilterValue(row, rowIdx, columnKey)
          : getNonGenericColumnFilterValue(row, columnKey);
        const normalizedRow = getNormalizedValue(rowValue);
        return selectedValues.some((value) => normalizedRow.includes(getNormalizedValue(value)));
      }
      const rowValue = isGeneric
        ? getGenericColumnFilterValue(row, rowIdx, columnKey)
        : getNonGenericColumnFilterValue(row, columnKey);
      const primaryMatch = evaluateFilterCondition(rowValue, filterState.mode, filterState.value);

      if (!isEmptyMode(filterState.mode)) return primaryMatch;

      const hasSecondaryFilter = modeRequiresInput(filterState.secondaryMode)
        ? getNormalizedValue(filterState.secondaryValue).length > 0
        : true;
      if (!hasSecondaryFilter) return primaryMatch;

      const secondaryMatch = evaluateFilterCondition(rowValue, filterState.secondaryMode, filterState.secondaryValue);
      return filterState.combine === 'or'
        ? (primaryMatch || secondaryMatch)
        : (primaryMatch && secondaryMatch);
    }));
  }, [
    columnFilters,
    getColumnFilterState,
    getGenericColumnFilterValue,
    getNonGenericColumnFilterValue,
    isCheckboxFilterColumn,
    isColumnFilterActive,
    isGeneric,
    list,
    enableColumnFilters,
    useServerSideColumnFilters
  ]);

  const total = filteredList.length;
  const startIndex = page && itemsPerPage ? (page - 1) * itemsPerPage : 0;
  const endIndex = itemsPerPage ? startIndex + itemsPerPage : 16;

  const visibleItems = showAll
    ? (serverSide ? filteredList.slice(0, itemsPerPage || 8) : filteredList.slice(startIndex, endIndex))
    : filteredList.slice(0, 16);

  React.useEffect(() => {
    if (typeof onCountChange === 'function') {
      onCountChange(total, visibleItems.length);
    }
  }, [page, itemsPerPage, showAll, total, visibleItems.length, agronomos, items, data]);

  React.useLayoutEffect(() => {
    const calculateHeights = () => {
      const newHeights = visibleItems.map((_, rowIdx) => {
        const rowContentRefs = Array.isArray(scrollCellRefs.current[rowIdx]) ? scrollCellRefs.current[rowIdx] : [];
        const rowContainerRefs = Array.isArray(cellRefs.current[rowIdx]) ? cellRefs.current[rowIdx] : [];
        const totalCells = Math.max(rowContentRefs.length, rowContainerRefs.length);
        const rowHeightCandidates = Array.from({ length: totalCells }, (_, colIdx) => {
          const contentEl = rowContentRefs[colIdx];
          const containerEl = rowContainerRefs[colIdx];
          if (!contentEl && !containerEl) return 0;

          const measurableContentEl = contentEl || containerEl?.firstElementChild;
          const contentHeight = measurableContentEl
            ? Math.max(measurableContentEl.scrollHeight || 0, measurableContentEl.getBoundingClientRect().height || 0)
            : 0;

          if (!containerEl) return contentHeight;

          const style = window.getComputedStyle(containerEl);
          const paddingTop = Number.parseFloat(style.paddingTop || '0') || 0;
          const paddingBottom = Number.parseFloat(style.paddingBottom || '0') || 0;
          const borderTop = Number.parseFloat(style.borderTopWidth || '0') || 0;
          const borderBottom = Number.parseFloat(style.borderBottomWidth || '0') || 0;
          const containerFixedSpace = paddingTop + paddingBottom + borderTop + borderBottom;

          return Math.ceil(Math.max(contentHeight + containerFixedSpace, containerFixedSpace));
        });

        if (rowHeightCandidates.length === 0) return 0;
        return Math.max(...rowHeightCandidates, 0);
      });
      setRowHeights((previousHeights) => (
        areHeightsEqual(previousHeights, newHeights) ? previousHeights : newHeights
      ));
    };

    calculateHeights();
    const frame = window.requestAnimationFrame(calculateHeights);
    window.addEventListener('resize', calculateHeights);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', calculateHeights);
    };
  }, [visibleItems, isGeneric, computedColumns]);

  React.useEffect(() => {
    if (!isGeneric) return;

    const calculateOverflow = () => {
      const nextMap = {};

      visibleItems.forEach((_, rowIdx) => {
        computedColumns.forEach((col, colIdx) => {
          if (!col?.enableHorizontalScroll) return;
          const el = scrollCellRefs.current?.[rowIdx]?.[colIdx];
          if (!el) return;
          const cellKey = `${rowIdx}-${colIdx}`;
          nextMap[cellKey] = el.scrollWidth > el.clientWidth + 1;
        });
      });

      setOverflowMap(nextMap);
    };

    const frame = window.requestAnimationFrame(calculateOverflow);
    window.addEventListener('resize', calculateOverflow);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', calculateOverflow);
    };
  }, [isGeneric, visibleItems, computedColumns, rowHeights]);

  React.useEffect(() => {
    if (!enableColumnFilters) {
      setActiveFilterColumnKey(null);
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (!activeFilterColumnKey) return;
      if (filtersPanelRef.current?.contains(event.target)) return;
      if (filterTriggerRefs.current?.[activeFilterColumnKey]?.contains(event.target)) return;
      setActiveFilterColumnKey(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [activeFilterColumnKey, enableColumnFilters]);

  React.useEffect(() => {
    if (!activeFilterColumnKey) return undefined;

    const reposition = () => updateFilterPanelPosition(activeFilterColumnKey);
    reposition();

    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [activeFilterColumnKey, updateFilterPanelPosition]);

  const renderHeaderWithFilter = (label, columnKey, allowFilter = true, isLastColumn = false) => {
    const shouldShowFilter = enableColumnFilters && allowFilter && !!columnKey;
    const shouldRenderFilterSlot = enableColumnFilters && !!columnKey;
    const filterState = shouldShowFilter ? getColumnFilterState(columnKey) : DEFAULT_FILTER_STATE;
    const columnDef = shouldShowFilter ? getColumnDefinitionByKey(columnKey) : null;
    const isCheckboxFilter = shouldShowFilter && columnDef?.filterType === 'checkbox';
    const checkboxOptions = isCheckboxFilter && Array.isArray(columnDef?.filterOptions) ? columnDef.filterOptions : [];
    const maxCheckboxLabelLength = checkboxOptions.reduce((max, option) => {
      const labelLength = String(option?.label ?? option?.value ?? '').length;
      return Math.max(max, labelLength);
    }, 0);
    const panelWidth = isCheckboxFilter
      ? (columnDef?.filterPanelWidth ?? (maxCheckboxLabelLength > 12 ? '280px' : '240px'))
      : '230px';
    const panelBorderRadius = isCheckboxFilter ? '8px' : '6px';
    const panelPadding = isCheckboxFilter ? '14px' : '10px';
    const panelGap = isCheckboxFilter ? '10px' : '8px';
    const selectedCheckboxValues = Array.isArray(filterState.selectedValues) ? filterState.selectedValues : [];
    const hasActiveFilter = shouldShowFilter ? isColumnFilterActive(columnKey) : false;
    const modeHidesInput = isEmptyMode(filterState.mode);
    const isPanelOpen = activeFilterColumnKey === columnKey;

    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0, borderRight: isLastColumn ? 'none' : '1px solid rgba(255, 255, 255, 0.35)' }}>
          <TaglineP
            color={theme.colors.white.Branco}
            fontWeight={600}
            style={{ whiteSpace: 'nowrap', paddingRight: '8px' }}
          >
            {label}
          </TaglineP>

          {shouldRenderFilterSlot ? (
            shouldShowFilter ? (
              <button
                ref={(element) => setFilterTriggerRef(columnKey, element)}
                type="button"
                aria-label={`Filtrar ${label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setActiveFilterColumnKey((previous) => {
                    const nextKey = previous === columnKey ? null : columnKey;
                    if (nextKey && typeof window !== 'undefined') {
                      window.requestAnimationFrame(() => updateFilterPanelPosition(nextKey));
                    }
                    return nextKey;
                  });
                }}
                style={{
                  all: 'unset',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  borderRight: isLastColumn ? 'none' : '1px solid rgba(255, 255, 255, 0.35)',
                  boxSizing: 'border-box'
                }}
              >
                <FilterIcon active={hasActiveFilter || isPanelOpen} />
              </button>
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  borderRight: isLastColumn ? 'none' : '1px solid rgba(255, 255, 255, 0.35)',
                  boxSizing: 'border-box',
                  opacity: 0
                }}
              >
                <FilterIcon active={false} />
              </div>
            )
          ) : null}
        </div>

        {shouldShowFilter && isPanelOpen ? (
          createPortal(
            <div
              ref={filtersPanelRef}
              onClick={(event) => event.stopPropagation()}
              style={{
                position: 'fixed',
                top: `${filterPanelPosition.top}px`,
                left: `${filterPanelPosition.left}px`,
                width: panelWidth,
                zIndex: 30001,
                background: '#fff',
                border: '1px solid #D6DCE5',
                borderRadius: panelBorderRadius,
                boxShadow: '0 8px 18px rgba(16, 24, 40, 0.18)',
                padding: panelPadding,
                display: 'flex',
                flexDirection: 'column',
                alignItems: !isCheckboxFilter ? 'center' : 'flex-start',
                gap: panelGap
              }}
            >
              {isCheckboxFilter ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '260px', overflowY: 'auto', paddingRight: '2px' }}>
                    {checkboxOptions.map((option) => {
                      const optionValue = String(option?.value ?? '');
                      const isChecked = selectedCheckboxValues.includes(optionValue);
                      const chipBg = option?.backgroundColor ?? '#FFFFFF';
                      const chipColor = option?.color ?? '#1F2937';
                      const optionIcon = option?.icon;
                      const chipDot = option?.dotColor ?? '#6B7280';
                      const chipMinHeight = option?.minHeight ?? '30px';
                      const chipPadding = option?.padding ?? '6px 12px';
                      const checkboxId = `${columnKey}-checkbox-${optionValue}`;
                      return (
                        <div
                          key={checkboxId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          <S.CheckboxInput
                            id={checkboxId}
                            type="checkbox"
                            checked={isChecked}
                            onChange={(event) => {
                              const nextValues = event.target.checked
                                ? [...selectedCheckboxValues, optionValue]
                                : selectedCheckboxValues.filter((value) => value !== optionValue);
                              handleFilterChange(columnKey, { selectedValues: nextValues, mode: 'contains', value: '' });
                            }}
                          />
                          <label
                            htmlFor={checkboxId}
                            style={{
                              cursor: 'pointer',
                              margin: 0,
                              width: 'auto'
                            }}
                          >
                            <div
                              style={{
                                width: 'auto',
                                minHeight: chipMinHeight,
                                padding: chipPadding,
                                borderRadius: '10rem',
                                backgroundColor: chipBg,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'space-evenly'
                              }}
                            >
                              {optionIcon ? (
                                <img
                                  src={optionIcon}
                                  alt=""
                                  aria-hidden="true"
                                  style={{ width: '14px', height: '14px', marginRight: '8px', flexShrink: 0 }}
                                />
                              ) : (
                                <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: chipDot, marginRight: '8px', flexShrink: 0 }} />
                              )}
                              <TaglinePSpan color={chipColor}>
                                {option?.label ?? optionValue}
                              </TaglinePSpan>
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip content="Limpar filtros" animation="fade" delay={[0, 100]} arrow placement="bottom">
                      <button
                        type="button"
                        aria-label="Limpar filtros"
                        onClick={() => clearColumnFilter(columnKey)}
                        style={{
                          all: 'unset',
                          width: '34px',
                          height: '34px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #CBD5E1',
                          borderRadius: '6px',
                          background: '#fff',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        <img src={cancelaIcon} alt="" aria-hidden="true" style={{ width: '20px', height: '20px' }} />
                      </button>
                    </Tooltip>
                  </div>
                </>
              ) : (
                <>
                  <select
                    value={filterState.mode}
                    onChange={(event) => handleFilterChange(columnKey, { mode: event.target.value })}
                    style={{
                      width: '100%',
                      height: '34px',
                      border: '1px solid #CBD5E1',
                      borderRadius: '6px',
                      padding: '0 34px 0 10px',
                      outline: 'none',
                      backgroundImage: `url(${arrowDownIcon})`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 10px center',
                      backgroundSize: '12px',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none'
                    }}
                  >
                    {FILTER_OPERATORS.map((operator) => (
                      <option key={operator.value} value={operator.value}>
                        {operator.label}
                      </option>
                    ))}
                  </select>

                  {!modeHidesInput ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="text"
                        value={filterState.value}
                        onChange={(event) => handleFilterChange(columnKey, { value: event.target.value })}
                        placeholder="Filtrar..."
                        style={{
                          width: '100%',
                          height: '34px',
                          border: '1px solid #CBD5E1',
                          borderRadius: '6px',
                          padding: '0 10px',
                          outline: 'none'
                        }}
                      />
                      <Tooltip content="Limpar filtros" animation="fade" delay={[0, 100]} arrow placement="bottom">
                        <button
                          type="button"
                          aria-label="Limpar filtros"
                          onClick={() => clearColumnFilter(columnKey)}
                          style={{
                            all: 'unset',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid #CBD5E1',
                            borderRadius: '6px',
                            background: '#fff',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        >
                          <img src={cancelaIcon} alt="" aria-hidden="true" style={{ width: '20px', height: '20px' }} />
                        </button>
                      </Tooltip>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '2px 2px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name={`combine-${columnKey}`}
                            checked={filterState.combine === 'and'}
                            onChange={() => handleFilterChange(columnKey, { combine: 'and' })}
                          />
                          <span style={{ fontSize: '14px' }}>E</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name={`combine-${columnKey}`}
                            checked={filterState.combine === 'or'}
                            onChange={() => handleFilterChange(columnKey, { combine: 'or' })}
                          />
                          <span style={{ fontSize: '14px' }}>Ou</span>
                        </label>
                      </div>

                      <select
                        value={filterState.secondaryMode}
                        onChange={(event) => handleFilterChange(columnKey, { secondaryMode: event.target.value })}
                        style={{
                          width: '100%',
                          height: '34px',
                          border: '1px solid #CBD5E1',
                          borderRadius: '6px',
                          padding: '0 34px 0 10px',
                          outline: 'none',
                          backgroundImage: `url(${arrowDownIcon})`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 10px center',
                          backgroundSize: '12px',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          appearance: 'none'
                        }}
                      >
                        {FILTER_OPERATORS.map((operator) => (
                          <option key={`${columnKey}-${operator.value}`} value={operator.value}>
                            {operator.label}
                          </option>
                        ))}
                      </select>

                      {!isEmptyMode(filterState.secondaryMode) ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="text"
                            value={filterState.secondaryValue}
                            onChange={(event) => handleFilterChange(columnKey, { secondaryValue: event.target.value })}
                            placeholder="Filtrar..."
                            style={{
                              width: '100%',
                              height: '34px',
                              border: '1px solid #CBD5E1',
                              borderRadius: '6px',
                              padding: '0 10px',
                              outline: 'none'
                            }}
                          />
                          <Tooltip content="Limpar filtros" animation="fade" delay={[0, 100]} arrow placement="bottom">
                            <button
                              type="button"
                              aria-label="Limpar filtros"
                              onClick={() => clearColumnFilter(columnKey)}
                              style={{
                                all: 'unset',
                                width: '34px',
                                height: '34px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #CBD5E1',
                                borderRadius: '6px',
                                background: '#fff',
                                cursor: 'pointer',
                                flexShrink: 0
                              }}
                            >
                              <img src={cancelaIcon} alt="" aria-hidden="true" style={{ width: '20px', height: '20px' }} />
                            </button>
                          </Tooltip>
                        </div>
                      ) : null}
                    </>
                  )}
                </>
              )}
            </div>,
            document.body
          )
        ) : null}
      </div>
    );
  };

  const loadingOverlay = isLoading ? (
    <div
      style={{
        position: 'absolute',
        top: '52px',
        right: 0,
        left: 0,
        bottom: 0,
        background: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 6,
        pointerEvents: 'all'
      }}
    >
      <Loading size="64px" />
    </div>
  ) : null;

  React.useEffect(() => {
    const realEl = realTableScrollRef.current;
    if (!realEl) return undefined;

    const updateFakeScrollbarMetrics = () => {
      const scrollWidth = realEl.scrollWidth || 0;
      const clientWidth = realEl.clientWidth || 0;
      const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);
      const hasHorizontalOverflow = scrollWidth > clientWidth + 1;
      const renderedTrackWidth = fakeScrollbarRef.current?.clientWidth || 0;
      const nextTrackWidth = Math.max(renderedTrackWidth || clientWidth, 0);
      const nextThumbWidth = hasHorizontalOverflow
        ? Math.max((clientWidth / scrollWidth) * nextTrackWidth, 36)
        : 0;
      const maxThumbOffset = Math.max(nextTrackWidth - nextThumbWidth, 0);
      const normalizedProgress = maxScrollLeft > 0
        ? Math.min(Math.max(realEl.scrollLeft / maxScrollLeft, 0), 1)
        : 0;
      const nextThumbOffset = normalizedProgress >= 0.995
        ? maxThumbOffset
        : normalizedProgress <= 0.005
          ? 0
          : (normalizedProgress * maxThumbOffset);

      setShowFakeScrollbar(hasHorizontalOverflow);
      setFakeTrackWidth(nextTrackWidth);
      setFakeThumbWidth(nextThumbWidth);
      setFakeThumbOffset(nextThumbOffset);
    };

    updateFakeScrollbarMetrics();
    realEl.addEventListener('scroll', updateFakeScrollbarMetrics, { passive: true });
    window.addEventListener('resize', updateFakeScrollbarMetrics);

    const ResizeObs = window.ResizeObserver;
    const observer = ResizeObs ? new ResizeObs(() => updateFakeScrollbarMetrics()) : null;
    if (observer) {
      observer.observe(realEl);
      if (realEl.firstElementChild) observer.observe(realEl.firstElementChild);
    }

    const timer = window.setTimeout(updateFakeScrollbarMetrics, 0);
    const raf = window.requestAnimationFrame(updateFakeScrollbarMetrics);
    return () => {
      realEl.removeEventListener('scroll', updateFakeScrollbarMetrics);
      window.removeEventListener('resize', updateFakeScrollbarMetrics);
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
    };
  }, [columns, data, agronomos, items, visibleItems.length, rowHeights.length, isLoading, emptyMessage, showAll, page, itemsPerPage, serverSide]);

  const handleFakeScrollbarClick = React.useCallback((event) => {
    if (isDraggingFakeScrollbarRef.current) return;
    const realEl = realTableScrollRef.current;
    const trackEl = fakeScrollbarRef.current;
    if (!realEl || !trackEl) return;

    const rect = trackEl.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const trackWidth = rect.width || fakeTrackWidth;
    if (!trackWidth || !fakeThumbWidth) return;

    const maxScrollLeft = Math.max(realEl.scrollWidth - realEl.clientWidth, 0);
    const maxThumbOffset = Math.max(trackWidth - fakeThumbWidth, 0);
    const desiredThumbOffset = Math.max(0, Math.min(clickX - (fakeThumbWidth / 2), maxThumbOffset));
    const nextScrollLeftRaw = maxThumbOffset > 0
      ? (desiredThumbOffset / maxThumbOffset) * maxScrollLeft
      : 0;
    const nextScrollLeft = Math.min(Math.max(nextScrollLeftRaw, 0), maxScrollLeft);
    realEl.scrollLeft = nextScrollLeft >= (maxScrollLeft - 2) ? maxScrollLeft : nextScrollLeft;
  }, [fakeThumbWidth, fakeTrackWidth]);

  const handleFakeThumbPointerDown = React.useCallback((event) => {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    isDraggingFakeScrollbarRef.current = true;
    setIsDraggingFakeScrollbar(true);
    dragStartClientXRef.current = event.clientX;
    dragStartThumbOffsetRef.current = fakeThumbOffset;
  }, [fakeThumbOffset]);

  React.useEffect(() => {
    const handlePointerMove = (event) => {
      if (!isDraggingFakeScrollbarRef.current) return;
      const realEl = realTableScrollRef.current;
      const trackEl = fakeScrollbarRef.current;
      if (!realEl || !trackEl) return;

      const deltaX = event.clientX - dragStartClientXRef.current;
      const nextOffsetRaw = dragStartThumbOffsetRef.current + deltaX;
      const maxThumbOffset = Math.max((trackEl.clientWidth || fakeTrackWidth) - fakeThumbWidth, 0);
      const nextOffset = Math.min(Math.max(nextOffsetRaw, 0), maxThumbOffset);
      const maxScrollLeft = Math.max(realEl.scrollWidth - realEl.clientWidth, 0);
      const nextScrollLeft = maxThumbOffset > 0
        ? (nextOffset / maxThumbOffset) * maxScrollLeft
        : 0;

      setFakeThumbOffset(nextOffset);
      realEl.scrollLeft = nextScrollLeft >= (maxScrollLeft - 2) ? maxScrollLeft : nextScrollLeft;
    };

    const stopDragging = () => {
      if (!isDraggingFakeScrollbarRef.current) return;
      isDraggingFakeScrollbarRef.current = false;
      setIsDraggingFakeScrollbar(false);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
    };
  }, [fakeThumbWidth, fakeTrackWidth]);

  const fakeScrollbar = (showFakeScrollbar && !isLoading && visibleItems.length > 0) ? (
    <S.FakeScrollbarOuter>
      <S.FakeScrollbarTrack
        ref={fakeScrollbarRef}
        aria-hidden="true"
        onClick={handleFakeScrollbarClick}
      >
        <S.FakeScrollbarInner
          onPointerDown={handleFakeThumbPointerDown}
          style={{
            width: `${fakeThumbWidth}px`,
            transform: `translateX(${fakeThumbOffset}px)`,
          }}
          active={isDraggingFakeScrollbar}
        />
      </S.FakeScrollbarTrack>
    </S.FakeScrollbarOuter>
  ) : null;
  const showEmptyOverlay = !isLoading && !!emptyMessage && visibleItems.length === 0;
  const emptyOverlay = showEmptyOverlay ? (
    <div
      style={{
        position: 'absolute',
        top: '52px',
        right: 0,
        left: 0,
        bottom: 0,
        background: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5,
        pointerEvents: 'none'
      }}
    >
      <TaglineP color={theme.colors.gray.Cinza_5} fontWeight={700}>
        {emptyMessage}
      </TaglineP>
    </div>
  ) : null;

  if (isGeneric) {
    const resolveRowId = (row, idx) => {
      if (typeof getRowId === 'function') return getRowId(row, idx);
      return row?.id ?? row?.agronomist_id ?? idx;
    };

    const isDisabled = (row) => (typeof isRowDisabled === 'function' ? !!isRowDisabled(row) : false);
    const canClickRow = (row, rowIdx) => {
      if (isDisabled(row)) return false;
      if (typeof onRowClick === 'function') return true;
      if (typeof getRowClickHref === 'function') {
        const href = getRowClickHref(row, rowIdx);
        return !!href;
      }
      return false;
    };
    const rowsToRender = isLoading ? [] : visibleItems;

    return (
      <S.ContainerTable
        ref={realTableScrollRef}
        hasGap={false}
        allowVerticalOverflow={enableColumnFilters}
        style={{
          position: 'relative',
          minHeight: (isLoading || showEmptyOverlay) ? '220px' : undefined,
          overflowX: (isLoading || showEmptyOverlay) ? 'hidden' : undefined,
          overflowY: (isLoading || showEmptyOverlay) ? 'hidden' : undefined
        }}
      >
        <S.ContainerTableColumn>
          {computedColumns.map((col, colIdx) => (
            <S.ContainerTableColumnV2
              key={col?.key ?? col?.label ?? colIdx}
              width={col?.width}
              minWidth={col?.minWidth}
              maxWidth={col?.maxWidth}
            >
              <S.ContainerColumnInfoTopBlack>
                <S.ContainerInfoTopLabel
                  minWidth={col?.minWidth}
                  width={col?.width ?? col?.minWidth}
                  maxWidth={col?.maxWidth}
                >
                  {renderHeaderWithFilter(
                    col?.label ?? '-',
                    col?.key,
                    (col?.key ?? '') !== '__actions__' && col?.enableFilter !== false,
                    colIdx === computedColumns.length - 1
                  )}
                </S.ContainerInfoTopLabel>
              </S.ContainerColumnInfoTopBlack>

                {rowsToRender.map((row, rowIdx) => {
                  const id = resolveRowId(row, rowIdx);
                  const cursor = canClickRow(row, rowIdx);
                  const href = typeof getRowClickHref === 'function' ? getRowClickHref(row, rowIdx) : null;
                  const customRowStyle = typeof getRowStyle === 'function' ? getRowStyle(row, rowIdx) : undefined;
                  const rowStyle = getStripedRowStyle(rowIdx, customRowStyle);
                  const enableHorizontalScroll = !!col?.enableHorizontalScroll;
                  const cellKey = `${rowIdx}-${colIdx}`;
                  const hasOverflow = !!overflowMap[cellKey];
                  const canHorizontalScroll = enableHorizontalScroll && hasOverflow;
                  const cellMinWidth = enableHorizontalScroll ? '100%' : (col?.minWidth ?? col?.width);
                  const cellMaxWidthContent = col?.maxWidthContent;
                  const cellWidth = col?.width;
                  const cellMaxWidth = col?.maxWidth ?? cellMaxWidthContent;

                  return (
                    <S.ContainerColumnCellContent
                      key={`${id}-${col?.key ?? colIdx}`}
                      firstChild={rowIdx === 0}
                      lastChild={rowIdx === rowsToRender.length - 1}
                      cursor={cursor}
                      onClick={() => {
                        if (!cursor) return;
                        if (typeof onRowClick === 'function') {
                          onRowClick(row, rowIdx);
                          return;
                        }
                        if (href) {
                          window.location.href = href;
                        }
                      }}
                      maxWidthContent={col?.maxWidthContent}
                      height={rowHeights[rowIdx]}
                      style={rowStyle}
                      ref={(el) => setCellRef(rowIdx, colIdx, el)}
                    >
                      <S.TableRowCell
                        width={cellWidth}
                        minWidth={cellMinWidth}
                        maxWidth={cellMaxWidth}
                        JustifyContent={col?.justifyContent}
                        enableHorizontalScroll={canHorizontalScroll}
                        showScrollHint={hasOverflow}
                        ref={(el) => setScrollCellRef(rowIdx, colIdx, el)}
                      >
                        {typeof col?.render === 'function' ? col.render(row, rowIdx) : null}
                      </S.TableRowCell>
                    </S.ContainerColumnCellContent>
                  );
                })}
            </S.ContainerTableColumnV2>
          ))}
        </S.ContainerTableColumn>
        {fakeScrollbar}
        {loadingOverlay}
        {emptyOverlay}
      </S.ContainerTable>
    );
  }

  return (
    <S.ContainerTable
      ref={realTableScrollRef}
      hasGap={false}
      allowVerticalOverflow={enableColumnFilters}
      style={{
        position: 'relative',
        minHeight: (isLoading || showEmptyOverlay) ? '220px' : undefined,
        overflowX: (isLoading || showEmptyOverlay) ? 'hidden' : undefined,
        overflowY: (isLoading || showEmptyOverlay) ? 'hidden' : undefined
      }}
    >
      <S.ContainerTableColumn>
        {hasActionsColumn && actionsPosition === 'first' ? (
          <S.ContainerTableColumnV2
            width={actionsColumn?.width ?? '140px'}
            minWidth={actionsColumn?.minWidth ?? actionsColumn?.width ?? '140px'}
            maxWidth={actionsColumn?.maxWidth}
          >
            <S.ContainerColumnInfoTopBlack>
              <S.ContainerInfoTopLabel
                minWidth={actionsColumn?.minWidth ?? actionsColumn?.width ?? '140px'}
                width={actionsColumn?.width ?? actionsColumn?.minWidth ?? '140px'}
                maxWidth={actionsColumn?.maxWidth}
              >
                {renderHeaderWithFilter(actionsColumn?.label ?? 'AÇÕES', null, false)}
              </S.ContainerInfoTopLabel>
            </S.ContainerColumnInfoTopBlack>

            {visibleItems.map((agronomo, idx) => {
              const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
              const rowStyle = getStripedRowStyle(idx);
              return (
                <S.ContainerColumnCellContent
                  key={`actions-${id}`}
                  firstChild={idx === 0}
                  lastChild={idx === visibleItems.length - 1}
                  cursor={false}
                  onClick={() => null}
                  height={rowHeights[idx]}
                  style={rowStyle}
                  ref={(el) => setCellRef(idx, 20, el)}
                >
                  <S.TableRowCell
                    width={actionsColumn?.width ?? '140px'}
                    minWidth={actionsColumn?.minWidth ?? actionsColumn?.width ?? '140px'}
                    maxWidth={actionsColumn?.maxWidth}
                    JustifyContent={actionsColumn?.justifyContent ?? 'center'}
                    ref={(el) => setScrollCellRef(idx, 20, el)}
                  >
                    {renderActionsContent(agronomo, idx)}
                  </S.TableRowCell>
                </S.ContainerColumnCellContent>
              );
            })}
          </S.ContainerTableColumnV2>
        ) : null}

                <S.ContainerTableColumnV2 width="120px">
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="100px" width="120px">
              {renderHeaderWithFilter('Status', 'status')}
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const defaultHref = enableDefaultRowNavigation && isActive ? `/agronomos/${id}` : null;
            const rowHref = typeof getRowClickHref === 'function' ? getRowClickHref(agronomo, idx) : defaultHref;
            const canNavigate = !!rowHref;
            const displayCreas = getDisplayCreas(agronomo);
            const rowStyle = getStripedRowStyle(idx);

            return (
              <S.ContainerColumnCellContent
                key={`status-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = rowHref) : null)}
                height={rowHeights[idx]}
                style={rowStyle}
                ref={(el) => setCellRef(idx, 4, el)}
              >
                <S.TableRowCell minWidth="170px">
                  {isActive && displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-status-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TextOrStatus
                            key={key}
                            maxWidth="auto"
                            type="status"
                            status={isCreaActive ? 2 : 3}
                            textSuccess="Ativo"
                            textError="Inativo"
                            textDefault="Ativo"
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <TextOrStatus
                      maxWidth="auto"
                      type="status"
                      status={isActive ? 2 : 3}
                      textSuccess="Ativo"
                      textError="Inativo"
                      textDefault="Ativo"
                    />
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="260px" width="260px">
              {renderHeaderWithFilter('Nome', 'nome')}
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const defaultHref = enableDefaultRowNavigation && isActive ? `/agronomos/${id}` : null;
            const rowHref = typeof getRowClickHref === 'function' ? getRowClickHref(agronomo, idx) : defaultHref;
            const canNavigate = !!rowHref;
            const nome = agronomo?.agronomist_nome ?? agronomo?.nome ?? '-';
            const rowStyle = getStripedRowStyle(idx);

            return (
              <S.ContainerColumnCellContent
                key={`nome-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = rowHref) : null)}
                height={rowHeights[idx]}
                style={rowStyle}
                ref={(el) => setCellRef(idx, 0, el)}
              >
                <S.TableRowCell minWidth="260px">
                  <BodyM
                    color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                    fontWeight={800}
                  >
                    {nome}
                  </BodyM>
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              {renderHeaderWithFilter('Documento', 'documento')}
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const defaultHref = enableDefaultRowNavigation && isActive ? `/agronomos/${id}` : null;
            const rowHref = typeof getRowClickHref === 'function' ? getRowClickHref(agronomo, idx) : defaultHref;
            const canNavigate = !!rowHref;
            const displayCreas = getDisplayCreas(agronomo);
            const rowStyle = getStripedRowStyle(idx);

            return (
              <S.ContainerColumnCellContent
                key={`doc-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = rowHref) : null)}
                height={rowHeights[idx]}
                style={rowStyle}
                ref={(el) => setCellRef(idx, 1, el)}
              >
                <S.TableRowCell minWidth="170px">
                  {displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-doc-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TaglineP
                            key={key}
                            color={
                              isActive
                                ? (isCreaActive ? theme.colors.gray.Cinza_2 : theme.colors.gray.Cinza_7)
                                : theme.colors.disabled.Desabilitado_Medio
                            }
                            fontWeight={600}
                            style={{ ...textInCellLabelStyle, overflowWrap: 'anywhere' }}
                          >
                            {formatCreaNumero(crea, agronomo)}
                          </TaglineP>
                        );
                      })}
                    </div>
                  ) : (
                    <TaglineP
                      color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                      fontWeight={600}
                    >
                      -
                    </TaglineP>
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              {renderHeaderWithFilter('Tipo', 'tipo')}
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const defaultHref = enableDefaultRowNavigation && isActive ? `/agronomos/${id}` : null;
            const rowHref = typeof getRowClickHref === 'function' ? getRowClickHref(agronomo, idx) : defaultHref;
            const canNavigate = !!rowHref;
            const displayCreas = getDisplayCreas(agronomo);
            const rowStyle = getStripedRowStyle(idx);

            return (
              <S.ContainerColumnCellContent
                key={`tipo-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = rowHref) : null)}
                height={rowHeights[idx]}
                style={rowStyle}
                ref={(el) => setCellRef(idx, 2, el)}
              >
                <S.TableRowCell minWidth="170px">
                  {displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-tipo-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TaglineP
                            key={key}
                            color={
                              isActive
                                ? (isCreaActive ? theme.colors.gray.Cinza_2 : theme.colors.gray.Cinza_7)
                                : theme.colors.disabled.Desabilitado_Medio
                            }
                            fontWeight={600}
                            style={{ ...textInCellLabelStyle, overflowWrap: 'anywhere' }}
                          >
                            {formatTipoRegistro(crea)}
                          </TaglineP>
                        );
                      })}
                    </div>
                  ) : (
                    <TaglineP
                      color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                      fontWeight={600}
                    >
                      -
                    </TaglineP>
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="110px" width="110px">
              {renderHeaderWithFilter('UF/Registro', 'ufRegistro')}
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const defaultHref = enableDefaultRowNavigation && isActive ? `/agronomos/${id}` : null;
            const rowHref = typeof getRowClickHref === 'function' ? getRowClickHref(agronomo, idx) : defaultHref;
            const canNavigate = !!rowHref;
            const displayCreas = getDisplayCreas(agronomo);
            const rowStyle = getStripedRowStyle(idx);

            return (
              <S.ContainerColumnCellContent
                key={`uf-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = rowHref) : null)}
                height={rowHeights[idx]}
                style={rowStyle}
                ref={(el) => setCellRef(idx, 3, el)}
              >
                <S.TableRowCell minWidth="110px">
                  {displayCreas.length > 0 ? (
                    <div style={cellLabelStyle}>
                      {displayCreas.map((crea, creaIndex) => {
                        const key = `${id}-uf-${crea?.id ?? creaIndex}`;
                        const isCreaActive = crea?.status === 1;
                        return (
                          <TaglineP
                            key={key}
                            color={
                              isActive
                                ? (isCreaActive ? theme.colors.gray.Cinza_2 : theme.colors.gray.Cinza_7)
                                : theme.colors.disabled.Desabilitado_Medio
                            }
                            fontWeight={600}
                            style={{ ...textInCellLabelStyle, overflowWrap: 'anywhere' }}
                          >
                            {formatRegistro(crea, agronomo)}
                          </TaglineP>
                        );
                      })}
                    </div>
                  ) : (
                    <TaglineP
                      color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                      fontWeight={600}
                    >
                      -
                    </TaglineP>
                  )}
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>

        <S.ContainerTableColumnV2>
          <S.ContainerColumnInfoTopBlack>
            <S.ContainerInfoTopLabel minWidth="170px" width="170px">
              {renderHeaderWithFilter('Última atualização', 'ultimaAtualizacao', true, true)}
            </S.ContainerInfoTopLabel>
          </S.ContainerColumnInfoTopBlack>

          {visibleItems.map((agronomo, idx) => {
            const id = agronomo?.agronomist_id ?? agronomo?.id ?? idx;
            const statusInfo = deriveStatus(agronomo);
            const isActive = statusInfo.known ? statusInfo.isActive : true;
            const defaultHref = enableDefaultRowNavigation && isActive ? `/agronomos/${id}` : null;
            const rowHref = typeof getRowClickHref === 'function' ? getRowClickHref(agronomo, idx) : defaultHref;
            const canNavigate = !!rowHref;
            const ultimaAtualizacao = agronomo?.modified ?? agronomo?.updated_at ?? agronomo?.updatedAt ?? null;
            const rowStyle = getStripedRowStyle(idx);

            return (
              <S.ContainerColumnCellContent
                key={`updated-${id}`}
                firstChild={idx === 0}
                lastChild={idx === visibleItems.length - 1}
                cursor={canNavigate}
                onClick={() => (canNavigate ? (window.location.href = rowHref) : null)}
                height={rowHeights[idx]}
                style={rowStyle}
                ref={(el) => setCellRef(idx, 5, el)}
              >
                <S.TableRowCell minWidth="170px">
                  <TaglineP
                    color={isActive ? theme.colors.gray.Cinza_2 : theme.colors.disabled.Desabilitado_Medio}
                    fontWeight={600}
                  >
                    {ultimaAtualizacao ? `Atualizado ${formatarData(ultimaAtualizacao)}` : '-'}
                  </TaglineP>
                </S.TableRowCell>
              </S.ContainerColumnCellContent>
            );
          })}
        </S.ContainerTableColumnV2>
      </S.ContainerTableColumn>
      {fakeScrollbar}
      {loadingOverlay}
      {emptyOverlay}
    </S.ContainerTable>
  );
};

export default TableWithLabelBlackV2;
